import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import type { Message, ChatMessage as ChatMessageRecord } from "../../types";
import { usePhaseSimulation } from "./usePhaseSimulation.ts";
import { generateId, MIN_RESPONSE_DELAY_MS } from "./useChat.utils.ts";

export function useChat() {
  const { sessionId: paramSessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [query, setQuery] = useState("");
  const [isSearched, setIsSearched] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(!!paramSessionId);
  const [sessionId, setSessionId] = useState<string | null>(paramSessionId || null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { phase, startPhaseSimulation } = usePhaseSimulation();

  const urlSessionId = paramSessionId || null;

  if (urlSessionId !== sessionId) {
    setSessionId(urlSessionId);
    if (urlSessionId === null) {
      setMessages([]);
      setIsSearched(false);
      setQuery("");
      setSelectedFile(null);
      setIsLoading(false);
    } else {
      setMessages([]);
      setIsSearched(false);
      setSelectedFile(null);
      setIsLoading(true);
    }
  }

  const handleSelectSession = useCallback((id: string | null) => {
    if (id === null) {
      if (window.location.pathname !== "/") {
        navigate("/");
      } else {
        setSessionId(null);
        setMessages([]);
        setIsSearched(false);
        setQuery("");
      }
    } else {
      navigate(`/chat/${id}`);
    }
  }, [navigate]);

  useEffect(() => {
    if (urlSessionId !== null && !isSearched) {
      let active = true;
      const load = async () => {
        try {
          const res = await fetch(`/api/history?sessionId=${urlSessionId}`);
          if (!active) return;
          if (res.ok) {
            const data: ChatMessageRecord[] = await res.json();
            setMessages(data.map(msg => ({
              id: generateId(),
              role: msg.role,
              content: msg.content,
              sources: msg.sources ? JSON.parse(msg.sources) : [],
              file: msg.file_name ? { name: msg.file_name, type: msg.file_type || "" } : null,
            })));
            setIsSearched(true);
          } else if (res.status === 403 || res.status === 404) {
            handleSelectSession(null);
          }
        } catch (error) {
          console.error("Failed to load history", error);
        } finally {
          if (active) setIsLoading(false);
        }
      };
      load();
      return () => {
        active = false;
      };
    }
  }, [urlSessionId, isSearched, handleSelectSession]);

  const handleSubmit = useCallback(async (e?: React.FormEvent, queryOverride?: string) => {
    if (e) e.preventDefault();
    let finalQuery = (queryOverride || query).trim();
    if (!finalQuery && selectedFile) finalQuery = t("common.document", "Dokument");
    if (!finalQuery || isLoading) return;

    if (!isSearched) setIsSearched(true);
    const userMessage: Message = { id: generateId(), role: "user", content: finalQuery, file: selectedFile ? { name: selectedFile.name, type: selectedFile.type } : undefined };
    setMessages(prev => [...prev, userMessage]);

    const fileToSend = selectedFile;
    setSelectedFile(null);
    setQuery("");
    setIsLoading(true);
    const stopSimulation = startPhaseSimulation();
    const startTime = Date.now();

    try {
      let res;
      if (fileToSend) {
        const formData = new FormData();
        formData.append("question", finalQuery);
        if (sessionId) formData.append("sessionId", sessionId);
        formData.append("file", fileToSend);
        res = await fetch("/api/chat", { method: "POST", body: formData });
      } else {
        res = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ question: finalQuery, sessionId }),
        });
      }
      if (!res.ok) throw new Error("Failed to fetch response");
      const data = await res.json();
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < MIN_RESPONSE_DELAY_MS) {
        await new Promise(resolve => setTimeout(resolve, MIN_RESPONSE_DELAY_MS - elapsedTime));
      }
      if (data.sessionId && sessionId === null) {
        setSessionId(data.sessionId);
        navigate(`/chat/${data.sessionId}`, { replace: true });
      }
      setMessages(prev => [...prev, { id: generateId(), role: "ai", content: data.answer, sources: data.sources }]);
    } catch (_error) {
      setMessages(prev => [...prev, { id: generateId(), role: "ai", content: t("common.chat_error", "Error occurred.") }]);
    } finally {
      stopSimulation();
      setIsLoading(false);
    }
  }, [query, isLoading, isSearched, sessionId, selectedFile, startPhaseSimulation, navigate, t]);

  return { query, setQuery, messages, isLoading, phase, isSearched, sessionId, selectedFile, setSelectedFile, handleSubmit, handleSelectSession };
}
